import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Shield, Bell, Lock, CheckCircle, BarChart3, FileText, Link2, CreditCard, Eye, Monitor, Smartphone, Tablet, Star, Zap, UserCheck, Settings, HeartHandshake, Gift, Home, Calendar, BookOpen, Scale, RefreshCw, Ban, MessageCircle, Receipt, EyeOff, Clock } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageCoParentingEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Co-Parenting | Collaborative Management"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="coparentalite" lang="en" />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <HeartHandshake size={16} />
            <span>Co-Parenting Module</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-parents)' }}>Co-Parenting</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--actor-parents)' }}>Collaborative management module for modern families</strong>
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">2+</div>
              <div className="stat-label">Connected parents</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">50/50</div>
              <div className="stat-label">Auto split</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Secure</div>
            </div>
          </div>
        </div>
      </section>

      {/* Collaborative Management Module */}
      <section id="gestion-collaborative" className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Collaborative Management Module</h2>
            <p className="section-subtitle">
              Shared access and configurable permissions for harmonious co-parenting
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Multi-Parent Access</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Invitation via unique link</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Double approval validation</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Real-time synchronization</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Settings size={32} /></div>
              <h3>Configurable Permissions</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Who can order</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Who can view</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Who can pay</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Enhanced Security</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Encrypted separate payments</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Complete activity log</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> IP traceability and timestamps</li>
              </ul>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Smart Notifications</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Real-time alerts</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Interactive approvals</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Action history</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Sharing */}
      <section className="section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Payment Sharing</h2>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Automatic Split</h3>
              <p>Predefined options: 50/50, 60/40, 70/30 or custom percentage</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Double Approval</h3>
              <p>Both parents must approve the split before activation</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Lock size={32} /></div>
              <h3>Encrypted Separate Payments</h3>
              <p>Each parent enters their payment method. Neither sees the other's information</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Individual Invoices</h3>
              <p>Automatic separate receipts. Exportable annual PDF report</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Link2 size={32} /></div>
              <h3>Unique Payment Link</h3>
              <p>Send a payment link to third parties (grandparents, family, friends) for one-time contribution without registration</p>
            </div>
          </div>
        </div>
      </section>

      {/* External Payment Link */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">External Payment Link — <span style={{color: 'var(--actor-parents)'}}>Unique Feature</span></h2>
            <p className="section-subtitle">
              Parent manages the complete order, third party pays via dedicated interface
            </p>
          </div>

          <h3 style={{color: 'white', marginBottom: '1.5rem', fontSize: '1.2rem'}}>How It Works</h3>

          <div className="benefits-grid">
            <div className="benefit-card" style={{borderTop: '3px solid #00D4FF'}}>
              <div style={{background: '#00D4FF', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>1</div>
              <h3>Parent creates the full order</h3>
              <p>Configures all meals, menus and preferences for their child(ren) via their portal</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #84CC16'}}>
              <div style={{background: '#84CC16', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>2</div>
              <h3>Custom link generation</h3>
              <p>System generates a unique and secure payment link for this specific order</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #FF8C00'}}>
              <div style={{background: '#FF8C00', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>3</div>
              <h3>Send to third-party payer</h3>
              <p>Parent sends the link (email, SMS, WhatsApp) to the person who will make the payment</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #C084FC'}}>
              <div style={{background: '#C084FC', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>4</div>
              <h3>Dedicated frontend interface</h3>
              <p>Third party accesses a simple and secure web page displaying the order and amount to pay</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #E74C3C'}}>
              <div style={{background: '#E74C3C', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>5</div>
              <h3>Payment without registration</h3>
              <p>Third party pays directly via platform (credit card) without creating an account</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #06B6D4'}}>
              <div style={{background: '#06B6D4', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem'}}>6</div>
              <h3>Instant confirmation</h3>
              <p>Parent and third party receive immediate confirmation with payment receipt</p>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="section roi-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem'}}>
            <div className="roi-card" style={{padding: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Star size={22} style={{color: '#FF8C00'}} /> Parent Benefits</h3>
              <ul style={{listStyle: 'none', padding: 0, lineHeight: '2.2', color: 'var(--color-text-secondary)'}}>
                <li><UserCheck size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Full control over orders and food choices</li>
                <li><BarChart3 size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Financial flexibility — delegate payment easily</li>
                <li><Eye size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Transparency — third party sees exactly what they pay</li>
                <li><FileText size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Complete history of who paid what and when</li>
              </ul>
            </div>
            <div className="roi-card" style={{padding: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Users size={22} style={{color: '#E74C3C'}} /> Third Party Benefits</h3>
              <ul style={{listStyle: 'none', padding: 0, lineHeight: '2.2', color: 'var(--color-text-secondary)'}}>
                <li><Zap size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Ultra simple — 1 click, payment, done</li>
                <li><CreditCard size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> No registration required to pay</li>
                <li><Shield size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Security — payment via certified platform</li>
                <li><FileText size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Instant receipt by email for proof</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Extension Parent Portal */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Extension Parent Portal</h2>
            <p className="section-subtitle">Dedicated Web + Mobile Interface</p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric"><Monitor size={28} style={{color: '#00D4FF'}} /><Tablet size={22} style={{color: '#00D4FF'}} /><Smartphone size={18} style={{color: '#00D4FF'}} /></div>
              <ul style={{listStyle: 'none', padding: 0, lineHeight: '2.2', color: 'var(--color-text-secondary)', marginTop: '1rem', textAlign: 'left'}}>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Dedicated Web + Mobile interface</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Shared dashboard for 2 parents</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Configurable granular permissions</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Automatic payment split</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Third-party payment page (no account)</li>
                <li><CheckCircle size={14} style={{color: 'var(--actor-parents)', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Complete history & audit</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Typical Use Cases */}
      <section className="section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Typical Use Cases</h2>
          </div>
          <div className="roi-grid">
            <div className="roi-card" style={{textAlign: 'center', padding: '2rem'}}>
              <HeartHandshake size={36} style={{color: '#00D4FF', marginBottom: '1rem'}} />
              <h3>Grandparents</h3>
              <p style={{color: 'var(--color-text-secondary)', marginTop: '0.5rem'}}>Contribute to their grandchildren's meals</p>
            </div>
            <div className="roi-card" style={{textAlign: 'center', padding: '2rem'}}>
              <Gift size={36} style={{color: '#FF8C00', marginBottom: '1rem'}} />
              <h3>Birthday Gift</h3>
              <p style={{color: 'var(--color-text-secondary)', marginTop: '0.5rem'}}>Give school meals as a useful gift</p>
            </div>
            <div className="roi-card" style={{textAlign: 'center', padding: '2rem'}}>
              <Users size={36} style={{color: '#C084FC', marginBottom: '1rem'}} />
              <h3>Extended Family</h3>
              <p style={{color: 'var(--color-text-secondary)', marginTop: '0.5rem'}}>Uncles, aunts, godparents who want to help</p>
            </div>
          </div>
        </div>
      </section>

      {/* Usage Scenarios */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Usage Scenarios</h2>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Shared Custody</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Week A/B</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Alternating weekends</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> School holidays</li>
              </ul>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><HeartHandshake size={32} /></div>
              <h3>Involved Grandparents</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Read-only access</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Children tracking</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Optional financial participation</li>
              </ul>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Separated Parents</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Total transparency</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Avoids financial conflicts</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Smooth communication</li>
              </ul>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Scale size={32} /></div>
              <h3>Multiple Legal Guardians</h3>
              <ul style={{textAlign: 'left', color: 'var(--color-text-secondary)', lineHeight: '2', listStyle: 'none', padding: 0}}>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Configurable rights</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Multi-actor management</li>
                <li><CheckCircle size={14} style={{color: '#84CC16', marginRight: '0.5rem', verticalAlign: 'middle'}} /> Legal compliance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Key Advantages</h2>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Total Transparency</h3>
              <p>All actions visible. Complete shared history.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Ban size={32} /></div>
              <h3>Avoids Duplicates</h3>
              <p>Synchronized orders. Anti-duplicate alerts.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><MessageCircle size={32} /></div>
              <h3>Smooth Communication</h3>
              <p>Contextual notifications. 1-click validation.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Receipt size={32} /></div>
              <h3>Clear Financial Management</h3>
              <p>Automatic split. Separate invoices. Detailed reports.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><EyeOff size={32} /></div>
              <h3>Guaranteed Privacy</h3>
              <p>Encrypted banking data. Granular permissions.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><RefreshCw size={32} /></div>
              <h3>Real-Time Sync</h3>
              <p>Instant updates. 0 conflicts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
          </div>
          <div className="benefits-grid" style={{maxWidth: '900px', margin: '0 auto', gridTemplateColumns: 'repeat(2, 1fr)'}}>
            <div className="benefit-card" style={{borderTop: '3px solid #00D4FF'}}>
              <div style={{background: '#00D4FF', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>1</div>
              <h3>Setup</h3>
              <p>Primary parent configures and invites the other parent</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #84CC16'}}>
              <div style={{background: '#84CC16', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>2</div>
              <h3>Acceptance</h3>
              <p>Invited parent accepts and creates their account</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #FF8C00'}}>
              <div style={{background: '#FF8C00', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>3</div>
              <h3>Validation</h3>
              <p>Both parents validate the payment split</p>
            </div>
            <div className="benefit-card" style={{borderTop: '3px solid #C084FC'}}>
              <div style={{background: '#C084FC', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#0A0F1E', marginBottom: '0.8rem', fontSize: '1.1rem'}}>4</div>
              <h3>Automation</h3>
              <p>Future transactions are automatically shared</p>
            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>
              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-legal">
              <span>&copy; 2025 EcoLunch. All rights reserved.</span>
              <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontWeight: 600, textDecoration: 'none'}}>ecotechsysteme.ca</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageCoParentingEN;
